import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js/min';

/**
 * Validates a phone number.
 * A leading '+' is considered just a country prefix.
 */
export const isPhoneNumberValid = (
    phoneNumberString: string,
    countryCode?: CountryCode
): boolean => {
    const trimmed = phoneNumberString.trim();
    if (!trimmed) return false;

    // Normalize white space, hyphens, parentheses and leading 00 → +
    const normalized = trimmed.replace(/\s+/g, '').replace(/[()-]/g, '').replace(/^00/, '+');

    if (!normalized.startsWith('+') && !countryCode) return false;

    const parsed = parsePhoneNumberFromString(
        normalized,
        normalized.startsWith('+') ? undefined : countryCode
    );

    return parsed?.isValid() ?? false;
};

/**
 * Combines a dialing code and a local number, then validates the full number.
 */
export const validateCombinedPhoneNumber = (dialingCode: string, phoneNumber: string): boolean => {
    const normalizedDialingCode = dialingCode.startsWith('+') ? dialingCode : `+${dialingCode}`;
    const normalizedPhone = phoneNumber.replace(/\s+/g, '').replace(/[()-]/g, '');
    const fullNumber = `${normalizedDialingCode}${normalizedPhone.trim().replace(/^0+/, '')}`;
    return isPhoneNumberValid(fullNumber);
};

export const getPhoneNumberHelper = (
    phoneNumber: string,
    isValid: boolean,
    invalidMessage: string
) => (phoneNumber.trim().length > 5 && !isValid ? invalidMessage : undefined);

export function formatPhoneNumber(phoneNumber: string): string {
    const number = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
    try {
        return parsePhoneNumber(number).format('INTERNATIONAL');
    } catch {
        return number;
    }
}

/**
 * Updates a phone number with a new calling code.
 * If the number already starts with the normalized calling code, it is returned unchanged.
 * Otherwise, it removes a leading zero from the number and prepends the calling code.
 */
export const updatePhoneNumberWithCountry = (
    currentPhoneNumber: string,
    callingCode: string
): string => {
    const normalizedCallingCode = callingCode.startsWith('+') ? callingCode : `+${callingCode}`;
    const trimmed = currentPhoneNumber.trim();
    if (trimmed.startsWith(normalizedCallingCode)) {
        return trimmed;
    }
    const localPart = trimmed.startsWith('0') ? trimmed.substring(1) : trimmed;
    return `${normalizedCallingCode}${localPart}`;
};
